<script setup lang="ts">
/**
 * GSheetDatepicker - A responsive date picker component
 *
 * Desktop: Uses Headless UI Listbox dropdowns for month, day, year
 * Mobile: Uses action sheet with iOS-style scroll picker wheels
 */
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import { getDaysInMonth, isValid } from 'date-fns'
import { isNumber } from 'lodash-es'
import { computed, onMounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { DEFAULT_YEAR_FOR_OPTIONAL_YEAR, formatDate, parseDate } from '@consumer/utils/date'
import { faCalendar, faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { isMobile } from '@consumer/utils/breakpoints'
import { preventWindowScroll } from '@shared/composables'

const {
  modelValue,
  disabled = false,
  hasErrors = false,
  size = 'large',
  hideYear = false,
  optionalYear = false,
  shortMonths = false,
  defaultYear = DEFAULT_YEAR_FOR_OPTIONAL_YEAR,
  defaultSelectedYear,
  placeholder = 'Select date',
  title = 'Select Date',
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  format = 'MMMM d, yyyy',
  pill = false,
  noFutureYears = false,
  alwaysSheet = false,
} = defineProps<{
  modelValue?: string | null // 'yyyy-MM-dd' format
  disabled?: boolean
  hasErrors?: boolean
  size?: 'small' | 'large'
  hideYear?: boolean // Hides year picker entirely
  optionalYear?: boolean // Shows year picker but allows it to be empty
  shortMonths?: boolean // Use short month names (Jan, Feb, etc.)
  defaultYear?: number
  defaultSelectedYear?: number
  placeholder?: string
  title?: string
  cancelLabel?: string
  confirmLabel?: string
  format?: string
  pill?: boolean
  noFutureYears?: boolean // Caps year options at the current year (e.g. for birthdays)
  alwaysSheet?: boolean // Force the single-field action-sheet picker even on desktop
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'update:incomplete': [value: boolean]
  focus: []
  blur: []
  cancel: []
  confirm: [value: string | null]
}>()

// =============================================================================
// State
// =============================================================================

const isActionSheet = computed(() => alwaysSheet || isMobile.value)
const mobileOpen = ref(false)

// Committed selection (synced with modelValue)
const selectedMonth = ref<number | null>(null)
const selectedDay = ref<number | null>(null)
const selectedYear = ref<number | null>(null)

// Pending selection for mobile (before confirm)
const pendingMonth = ref<number | null>(null)
const pendingDay = ref<number | null>(null)
const pendingYear = ref<number | null>(null)

// =============================================================================
// Options
// =============================================================================

const longMonthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const monthOptions = computed(() => {
  const names = shortMonths ? shortMonthNames : longMonthNames
  const all = names.map((label, value) => ({ label, value }))
  if (!noFutureYears) return all

  const today = new Date()
  const year = isActionSheet.value ? pendingYear.value : selectedYear.value
  if (year === today.getFullYear()) {
    return all.filter(o => o.value <= today.getMonth())
  }
  return all
})

const daysInSelectedMonth = computed(() => {
  const month = isActionSheet.value ? (pendingMonth.value ?? 0) : (selectedMonth.value ?? 0)
  const year = isActionSheet.value
    ? (pendingYear.value ?? 2000)
    : (selectedYear.value ?? 2000)
  return getDaysInMonth(new Date(year, month))
})

const dayOptions = computed(() => {
  const all = Array.from(
    { length: daysInSelectedMonth.value },
    (_, i) => ({ label: String(i + 1), value: i + 1 }),
  )
  if (!noFutureYears) return all

  const today = new Date()
  const year = isActionSheet.value ? pendingYear.value : selectedYear.value
  const month = isActionSheet.value ? pendingMonth.value : selectedMonth.value
  if (year === today.getFullYear() && month === today.getMonth()) {
    return all.filter(o => o.value <= today.getDate())
  }
  return all
})

const yearOptions = computed(() => {
  const startYear = new Date().getFullYear() + (noFutureYears ? 0 : 1)
  const years = Array.from(
    { length: 100 },
    (_, i) => ({ label: String(startYear - i), value: startYear - i }),
  )
  // Preserve an out-of-range existing selection so legacy data (e.g. a future
  // year saved before noFutureYears was enforced) remains viewable/editable.
  const existing = isActionSheet.value ? pendingYear.value : selectedYear.value
  if (existing != null && !years.some(y => y.value === existing)) {
    const entry = { label: String(existing), value: existing }
    if (existing > startYear) years.unshift(entry)
    else years.push(entry)
  }
  // Add empty option at the start for optional year (mobile picker wheel)
  if (optionalYear) {
    years.unshift({ label: '—', value: null as any })
  }
  return years
})

// =============================================================================
// Computed Display Values
// =============================================================================

const displayValue = computed(() => {
  if (selectedMonth.value === null || selectedDay.value === null) {
    return ''
  }
  const year = selectedYear.value ?? defaultYear
  const date = new Date(year, selectedMonth.value, selectedDay.value)
  if (!isValid(date)) return ''

  // If hideYear/optionalYear and no year selected, format without year
  if ((hideYear || optionalYear) && selectedYear.value === null) {
    return formatDate(date, { format: 'MMMM d' })
  }
  return formatDate(date, { format })
})

// =============================================================================
// Parse and Emit
// =============================================================================

function parseModelValue (val: string | null) {
  const parsedDate = val ? parseDate(val, 'yyyy-MM-dd') : null
  if (parsedDate && isValid(parsedDate)) {
    // If hideYear/optionalYear and date uses default year, treat as no year selected
    const parsedYear = parsedDate.getFullYear()
    selectedYear.value = (hideYear || optionalYear) && parsedYear === defaultYear
      ? null
      : parsedYear
    selectedMonth.value = parsedDate.getMonth()
    selectedDay.value = parsedDate.getDate()
  }
  else {
    selectedYear.value = null
    selectedMonth.value = null
    selectedDay.value = null
  }
}

function emitValue (year: number | null, month: number | null, day: number | null) {
  const effectiveYear = (!year && (hideYear || optionalYear)) ? defaultYear : year

  if (isNumber(effectiveYear) && isNumber(month) && isNumber(day)) {
    const days = getDaysInMonth(new Date(effectiveYear, month))
    if (day <= days) {
      const newDate = new Date(effectiveYear, month, day)
      if (isValid(newDate)) {
        const y = String(newDate.getFullYear()).padStart(4, '0')
        const m = String(newDate.getMonth() + 1).padStart(2, '0')
        const d = String(newDate.getDate()).padStart(2, '0')
        const emittedValue = `${y}-${m}-${d}`
        emit('update:modelValue', emittedValue)
        return emittedValue
      }
    }
  }
  return null
}

// Initialize from modelValue
onMounted(() => {
  parseModelValue(modelValue ?? null)
})

// React to parent prop changes
watch(() => modelValue, (val) => {
  parseModelValue(val ?? null)
})

// Desktop: emit on any change
watch([selectedYear, selectedMonth, selectedDay], ([year, month, day]) => {
  if (!isActionSheet.value) {
    emitValue(year, month, day)
  }
})

// A partial selection emits no value, so parents can't tell it apart from an empty picker.
const incomplete = computed(() => {
  const anySelected = selectedMonth.value !== null || selectedDay.value !== null || selectedYear.value !== null
  const complete = selectedMonth.value !== null && selectedDay.value !== null
    && (selectedYear.value !== null || optionalYear || hideYear)
  return anySelected && !complete
})

// immediate resets stale parent state after an unmount/remount
watch(incomplete, value => emit('update:incomplete', value), { immediate: true })

// =============================================================================
// Desktop: Combobox query state per field
// =============================================================================
// Combobox supports typed filtering natively. The three fields keep their
// own query string so the user can type "jun" / "15" / "1995" to filter.

const monthQuery = ref('')
const dayQuery = ref('')
const yearQuery = ref('')

const filteredMonthOptions = computed(() => {
  const q = monthQuery.value.trim().toLowerCase()
  if (!q) return monthOptions.value
  return monthOptions.value.filter(o => o.label.toLowerCase().startsWith(q))
})

const filteredDayOptions = computed(() => {
  const q = dayQuery.value.trim()
  if (!q) return dayOptions.value
  return dayOptions.value.filter(o => o.label.startsWith(q))
})

const filteredYearOptions = computed(() => {
  const q = yearQuery.value.trim()
  if (!q) return yearOptions.value
  return yearOptions.value.filter(o => o.label.startsWith(q))
})

const scrollTargetYear = computed(() => {
  if (selectedYear.value != null) return selectedYear.value
  if (!hideYear && defaultSelectedYear != null) return defaultSelectedYear
  return null
})

// Block non-digit keys on numeric-only fields (Day, Year). Still allows
// navigation/editing keys (Backspace, arrows, Tab, Enter, Escape, etc.) and
// platform shortcuts (Cmd/Ctrl + c/v/a/x/z).
function onNumericKeydown (event: KeyboardEvent) {
  if (typeof event.key !== 'string' || event.key.length !== 1) return
  if (event.metaKey || event.ctrlKey || event.altKey) return
  if (!/^\d$/.test(event.key)) event.preventDefault()
}

// Reset a query on blur so the filtered options list returns to its full
// set for the next focus. Defers via setTimeout so click-to-pick on an
// option fires before the reset. Headless UI already re-syncs the input's
// displayed text to `displayValue(modelValue)` on blur, so we only need to
// clear the query.
function resetQueryOnBlur (reset: () => void) {
  setTimeout(reset, 0)
}

// Scroll the year dropdown to the active target year as soon as its option
// mounts. Prefer the current selection, and only fall back to the default
// year when nothing has been selected yet.
const scrollTargetYearOptionEl = ref<HTMLElement | null>(null)

watch(scrollTargetYearOptionEl, (el) => {
  if (!el || scrollTargetYear.value == null) return
  const container = el.closest<HTMLElement>('.g-sheet-datepicker-options')
  if (!container) return
  // Center the target year within the dropdown's own scroll area only. Using
  // `el.scrollIntoView` also scrolls the window, which shifts the floating
  // dropdown as it opens (jarring for users, and a source of CI click flake).
  const containerRect = container.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()
  const delta = (elRect.top - containerRect.top) - (container.clientHeight - el.offsetHeight) / 2
  container.scrollTop += delta
})

function setScrollTargetYearOptionEl (el: Element | ComponentPublicInstance | null, optionValue: number | null) {
  if (optionValue === scrollTargetYear.value) {
    scrollTargetYearOptionEl.value = el instanceof HTMLElement ? el : null
  }
}

// =============================================================================
// Mobile Action Sheet Handlers
// =============================================================================

function openMobile () {
  if (disabled) return

  // Initialize pending values from current selection
  pendingMonth.value = selectedMonth.value ?? new Date().getMonth()
  pendingDay.value = selectedDay.value ?? new Date().getDate()
  pendingYear.value = selectedYear.value || defaultSelectedYear || null

  mobileOpen.value = true
  emit('focus')
}

function closeMobile () {
  mobileOpen.value = false
  emit('blur')
}

function onCancel () {
  emit('cancel')
  closeMobile()
}

function onConfirm () {
  // Apply pending values
  selectedMonth.value = pendingMonth.value
  selectedDay.value = pendingDay.value
  selectedYear.value = pendingYear.value

  const value = emitValue(pendingYear.value, pendingMonth.value, pendingDay.value)
  emit('confirm', value)
  closeMobile()
}

// Adjust day if it exceeds the days in the newly selected month
watch([pendingMonth, pendingYear], () => {
  if (pendingDay.value && pendingDay.value > daysInSelectedMonth.value) {
    pendingDay.value = daysInSelectedMonth.value
  }
})

// When noFutureYears narrows month/day options (because the user moved to the
// current year/month), clamp any out-of-range selection to the latest valid
// value so the picker never displays a future date.
watch(monthOptions, (opts) => {
  const max = opts[opts.length - 1]?.value
  if (max == null) return
  if (selectedMonth.value != null && !opts.some(o => o.value === selectedMonth.value)) {
    selectedMonth.value = max
  }
  if (pendingMonth.value != null && !opts.some(o => o.value === pendingMonth.value)) {
    pendingMonth.value = max
  }
})

watch(dayOptions, (opts) => {
  const max = opts[opts.length - 1]?.value
  if (max == null) return
  if (selectedDay.value != null && !opts.some(o => o.value === selectedDay.value)) {
    selectedDay.value = max
  }
  if (pendingDay.value != null && !opts.some(o => o.value === pendingDay.value)) {
    pendingDay.value = max
  }
})

// =============================================================================
// Transitions
// =============================================================================

const sheetTransition = {
  enter: 'transition ease-in-out duration-300 transform',
  enterFrom: 'translate-y-full',
  enterTo: 'translate-y-0',
  leave: 'transition ease-in-out duration-300 transform',
  leaveFrom: 'translate-y-0',
  leaveTo: 'translate-y-full',
}

preventWindowScroll({ when: () => mobileOpen.value && isActionSheet.value })

// =============================================================================
// Expose
// =============================================================================

defineExpose({
  expand: openMobile,
  collapse: closeMobile,
})
</script>

<template>
  <div class="g-sheet-datepicker w-full">
    <!-- ===== Mobile: Trigger + Action Sheet ===== -->
    <template v-if="isActionSheet">
      <!-- Trigger Button -->
      <button
        type="button"
        :class="[
          'g-sheet-datepicker-trigger w-full flex items-center justify-between gap-2',
          'bg-white font-medium text-left text-navy cursor-pointer',
          { disabled, 'has-errors': hasErrors, [size]: true, pill },
        ]"
        :disabled="disabled"
        @click="openMobile"
      >
        <span
          class="g-sheet-datepicker-value flex-1 truncate"
          :class="{ placeholder: !displayValue, 'text-v2-tertiary': !displayValue }"
        >
          {{ displayValue || placeholder }}
        </span>
        <GIcon
          class="g-sheet-datepicker-icon shrink-0"
          :class="{ 'opacity-20': disabled }"
          :icon="faCalendar"
          size="sm"
        />
      </button>

      <!-- Action Sheet -->
      <TransitionRoot as="template" :show="mobileOpen">
        <Dialog as="div" class="fixed inset-0 z-dialog flex items-end" @close="onCancel">
          <!-- Backdrop -->
          <TransitionChild
            as="template"
            enter="transition-opacity ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in-out duration-700"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div class="fixed inset-0 bg-black/40"/>
          </TransitionChild>

          <!-- Panel -->
          <TransitionChild as="template" v-bind="sheetTransition">
            <DialogPanel class="relative bg-white w-full rounded-t-v2lg flex flex-col overflow-hidden">
              <!-- Header -->
              <div class="flex items-center justify-between px-4 py-4">
                <button type="button" class="text-base font-medium text-v2-secondary" @click="onCancel">
                  {{ cancelLabel }}
                </button>
                <span class="text-base font-semibold text-navy">{{ title }}</span>
                <button type="button" class="text-base font-medium text-v2-active" @click="onConfirm">
                  {{ confirmLabel }}
                </button>
              </div>

              <!-- Picker Wheels -->
              <div class="flex gap-2 px-4 pb-8">
                <GPickerWheel
                  v-model="pendingMonth"
                  :options="monthOptions"
                />
                <GPickerWheel
                  v-model="pendingDay"
                  :options="dayOptions"
                />
                <GPickerWheel
                  v-if="!hideYear"
                  v-model="pendingYear"
                  :options="yearOptions"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </TransitionRoot>
    </template>

    <!-- ===== Desktop: Headless UI Comboboxes (type-to-filter) ===== -->
    <div v-else class="g-sheet-datepicker-desktop flex gap-2 w-full">
      <!-- Month -->
      <Combobox
        v-model="selectedMonth"
        :disabled="disabled"
        as="div"
        class="g-sheet-datepicker-select relative min-w-0 flex-1"
        @update:modelValue="monthQuery = ''"
      >
        <Float
          placement="bottom-start"
          :offset="4"
          flip
          shift
          portal
          adaptiveWidth
          enter="transition duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            :class="[
              'g-sheet-datepicker-select-btn w-full flex items-center justify-between gap-1',
              'bg-white font-medium text-navy',
              { disabled, 'has-errors': hasErrors, [size]: true, pill },
            ]"
          >
            <ComboboxInput
              class="g-sheet-datepicker-input flex-1 min-w-0 bg-transparent outline-none font-medium text-navy"
              size="1"
              :displayValue="(v: any) => v == null ? '' : (monthOptions.find(o => o.value === v)?.label ?? '')"
              placeholder="Month"
              :disabled="disabled"
              @change="monthQuery = ($event.target as HTMLInputElement).value"
              @blur="resetQueryOnBlur(() => { monthQuery = '' })"
            />
            <ComboboxButton
              class="g-sheet-datepicker-select-chevron-btn shrink-0 flex items-center justify-center cursor-pointer"
            >
              <GIcon class="g-sheet-datepicker-select-chevron shrink-0" :icon="faChevronDown" size="xs"/>
            </ComboboxButton>
          </div>

          <ComboboxOptions class="g-sheet-datepicker-options py-1 bg-white drop-shadow-dropdown overflow-auto">
            <ComboboxOption
              v-for="option in filteredMonthOptions"
              v-slot="{ active, selected }"
              :key="option.value"
              :value="option.value"
              as="template"
            >
              <li
                class="g-sheet-datepicker-option px-4 py-3 cursor-pointer font-medium text-navy select-none"
                :class="{ active, selected }"
              >
                {{ option.label }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </Float>
      </Combobox>

      <!-- Day -->
      <Combobox
        v-model="selectedDay"
        :disabled="disabled"
        as="div"
        class="g-sheet-datepicker-select relative min-w-0 flex-none w-22"
        @update:modelValue="dayQuery = ''"
      >
        <Float
          placement="bottom-start"
          :offset="4"
          flip
          shift
          portal
          adaptiveWidth
          enter="transition duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            :class="[
              'g-sheet-datepicker-select-btn w-full flex items-center justify-between gap-1',
              'bg-white font-medium text-navy',
              { disabled, 'has-errors': hasErrors, [size]: true, pill },
            ]"
          >
            <ComboboxInput
              class="g-sheet-datepicker-input flex-1 min-w-0 bg-transparent outline-none font-medium text-navy"
              size="1"
              :displayValue="(v: any) => v == null ? '' : (dayOptions.find(o => o.value === v)?.label ?? '')"
              placeholder="Day"
              inputmode="numeric"
              :disabled="disabled"
              @keydown="onNumericKeydown"
              @change="dayQuery = ($event.target as HTMLInputElement).value"
              @blur="resetQueryOnBlur(() => { dayQuery = '' })"
            />
            <ComboboxButton
              class="g-sheet-datepicker-select-chevron-btn shrink-0 flex items-center justify-center cursor-pointer"
            >
              <GIcon class="g-sheet-datepicker-select-chevron shrink-0" :icon="faChevronDown" size="xs"/>
            </ComboboxButton>
          </div>

          <ComboboxOptions class="g-sheet-datepicker-options py-1 bg-white drop-shadow-dropdown overflow-auto">
            <ComboboxOption
              v-for="option in filteredDayOptions"
              v-slot="{ active, selected }"
              :key="option.value"
              :value="option.value"
              as="template"
            >
              <li
                class="g-sheet-datepicker-option px-4 py-3 cursor-pointer font-medium text-navy select-none"
                :class="{ active, selected }"
              >
                {{ option.label }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </Float>
      </Combobox>

      <!-- Year -->
      <Combobox
        v-if="!hideYear"
        v-model="selectedYear"
        :disabled="disabled"
        as="div"
        class="g-sheet-datepicker-select relative min-w-0 flex-none w-25"
        @update:modelValue="yearQuery = ''"
      >
        <Float
          placement="bottom-start"
          :offset="4"
          flip
          shift
          portal
          adaptiveWidth
          enter="transition duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            :class="[
              'g-sheet-datepicker-select-btn w-full flex items-center justify-between gap-1',
              'bg-white font-medium text-navy',
              { disabled, 'has-errors': hasErrors, [size]: true, pill },
            ]"
          >
            <ComboboxInput
              class="g-sheet-datepicker-input flex-1 min-w-0 bg-transparent outline-none font-medium text-navy"
              size="1"
              :displayValue="(v: any) => v == null ? '' : (yearOptions.find(o => o.value === v)?.label ?? '')"
              placeholder="Year"
              inputmode="numeric"
              :disabled="disabled"
              @keydown="onNumericKeydown"
              @change="yearQuery = ($event.target as HTMLInputElement).value"
              @blur="resetQueryOnBlur(() => { yearQuery = '' })"
            />
            <ComboboxButton
              class="g-sheet-datepicker-select-chevron-btn shrink-0 flex items-center justify-center cursor-pointer"
            >
              <GIcon
                class="g-sheet-datepicker-select-chevron shrink-0"
                :icon="faChevronDown"
                size="xs"
              />
            </ComboboxButton>
          </div>

          <ComboboxOptions class="g-sheet-datepicker-options py-1 bg-white drop-shadow-dropdown overflow-auto">
            <ComboboxOption
              v-for="option in filteredYearOptions"
              v-slot="{ active, selected }"
              :key="option.value"
              :value="option.value"
              as="template"
            >
              <li
                :ref="(el) => setScrollTargetYearOptionEl(el, option.value)"
                class="g-sheet-datepicker-option px-4 py-3 cursor-pointer font-medium text-navy select-none"
                :class="{ active, selected }"
              >
                {{ option.label }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </Float>
      </Combobox>
    </div>
  </div>
</template>

<style lang="scss">
// =============================================================================
// Desktop Styles
// =============================================================================

.g-sheet-datepicker-select-btn {
  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.v2md");
  padding: 1rem;
  transition: border-color 0.15s;

  &.pill {
    border-radius: theme("borderRadius.full");
  }

  &.small {
    @apply py-2 px-3 text-sm rounded-v2sm;
  }

  &:hover:not(.disabled) {
    border-color: var(--theme-color-primary-base);
  }

  &:focus-within {
    border-color: var(--theme-color-primary-base);
    box-shadow: 0 0 0 1px var(--theme-color-primary-base);
    outline: none;
  }

  &.disabled {
    @apply opacity-50 cursor-not-allowed;
  }

  &.has-errors {
    border-color: theme("colors.red.base");
  }
}

.g-sheet-datepicker-input {
  &::placeholder {
    @apply text-v2-tertiary;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.g-sheet-datepicker-select-chevron-btn {
  background: transparent;
  border: none;
  padding: 0;

  &[aria-expanded="true"] .g-sheet-datepicker-select-chevron {
    transform: rotate(180deg);
  }
}

.g-sheet-datepicker-select-chevron {
  transition: transform 0.15s ease-out;
}

.g-sheet-datepicker-options {
  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.md");
  max-height: 200px;
  min-width: 100%;
}

.g-sheet-datepicker-option {
  &.active {
    background-color: var(--theme-color-surface-grey);
  }

  &.selected {
    color: var(--theme-color-text-v2-active);
  }
}

// =============================================================================
// Mobile Trigger Styles
// =============================================================================

.g-sheet-datepicker-trigger {
  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.v2md");
  padding: 1rem 1.25rem;
  transition: border-color 0.15s;

  &.pill {
    border-radius: theme("borderRadius.full");
  }

  &.small {
    @apply py-2 px-3 text-sm rounded-v2sm;
  }

  &:hover:not(.disabled) {
    border-color: var(--theme-color-primary-base);
  }

  &:focus {
    border-color: var(--theme-color-primary-base);
    box-shadow: 0 0 0 1px var(--theme-color-primary-base);
    outline: none;
    z-index: 10;
  }

  &.disabled {
    @apply opacity-50 cursor-not-allowed;
  }

  &.has-errors {
    border-color: theme("colors.red.base");
    color: theme("colors.red.base");
  }
}

.g-sheet-datepicker-chevron {
  transition: transform 0.1s ease-out;
}
</style>
